/**
 * YNS PDF Viewer
 * PDF.js-based viewer with annotation support.
 *
 * Features:
 * - PDF.js integration for in-browser rendering
 * - Highlight annotations (multiple colors)
 * - Sticky notes
 * - Bookmarks
 * - localStorage persistence for annotations
 * - Thumbnail navigation
 */

(function() {
  'use strict';

  // ============================================
  // Configuration
  // ============================================
  const CONFIG = {
    annotationsKey: 'yns_pdf_annotations',
    bookmarksKey: 'yns_pdf_bookmarks',
    lastPageKey: 'yns_pdf_last_page',
    defaultScale: 1.2,
    minScale: 0.5,
    maxScale: 3.0,
    scaleStep: 0.25,
    highlightColors: ['#FFEB3B', '#4CAF50', '#2196F3', '#E91E63'],
    defaultHighlightColor: '#FFEB3B'
  };

  // ============================================
  // State
  // ============================================
  let pdfDoc = null;
  let currentPage = 1;
  let totalPages = 0;
  let currentScale = CONFIG.defaultScale;
  let currentPdfId = null;
  let currentTool = null; // 'highlight', 'note', 'bookmark'
  let currentHighlightColor = CONFIG.defaultHighlightColor;
  let annotations = {};
  let bookmarks = {};
  let isRendering = false;
  let textLayer = null;

  // Freeform highlight drawing state
  let isDrawingHighlight = false;
  let highlightPoints = [];

  // Selection state for highlights (legacy)
  let selectionStart = null;
  let selectionEnd = null;

  // ============================================
  // DOM References
  // ============================================
  let modal = null;
  let canvas = null;
  let ctx = null;
  let annotationLayer = null;
  let canvasContainer = null;
  let canvasWrapper = null;

  // ============================================
  // Initialization
  // ============================================
  function init() {
    modal = document.getElementById('pdfViewerModal');
    canvas = document.getElementById('pdfCanvas');
    canvasContainer = document.getElementById('pdfCanvasContainer');
    canvasWrapper = document.getElementById('pdfCanvasWrapper');
    annotationLayer = document.getElementById('annotationLayer');

    if (!modal || !canvas) {
      console.warn('YNS PDF Viewer: Required elements not found');
      return;
    }

    ctx = canvas.getContext('2d');

    // Load saved data
    loadAnnotations();
    loadBookmarks();

    // Setup event listeners
    setupEventListeners();

    // Listen for annotation updates from study mode
    document.addEventListener('yns:annotationsUpdated', (e) => {
      if (e.detail.pdfId === currentPdfId) {
        loadAnnotations();
        renderAnnotations(currentPage);
        updateAnnotationsPanel();
      }
    });
  }

  // ============================================
  // Local Storage
  // ============================================
  function loadAnnotations() {
    try {
      const stored = localStorage.getItem(CONFIG.annotationsKey);
      annotations = stored ? JSON.parse(stored) : {};
    } catch (e) {
      annotations = {};
    }
  }

  function saveAnnotations() {
    try {
      localStorage.setItem(CONFIG.annotationsKey, JSON.stringify(annotations));

      // Sync annotations to Supabase
      if (window.YNS_SUPABASE && window.YNS_CONFIG?.customerId && currentPdfId) {
        const pdfAnnotations = annotations[currentPdfId] || [];
        pdfAnnotations.forEach(ann => {
          window.YNS_SUPABASE.queueSync(`annotation:${ann.id}`, async () => {
            await window.YNS_SUPABASE.upsert(window.YNS_SUPABASE.tables.pdfAnnotations, {
              id: ann.id,
              pdf_id: currentPdfId,
              annotation_type: ann.type,
              page_number: ann.page,
              points: ann.points || null,
              stroke_width: ann.strokeWidth || null,
              color: ann.color || null,
              x_position: ann.x || null,
              y_position: ann.y || null,
              content: ann.content || null,
              created_at: ann.createdAt
            }, ['id']);
          });
        });
      }
    } catch (e) {
      console.error('Error saving annotations:', e);
    }
  }

  function loadBookmarks() {
    try {
      const stored = localStorage.getItem(CONFIG.bookmarksKey);
      bookmarks = stored ? JSON.parse(stored) : {};
    } catch (e) {
      bookmarks = {};
    }
  }

  function saveBookmarks() {
    try {
      localStorage.setItem(CONFIG.bookmarksKey, JSON.stringify(bookmarks));
    } catch (e) {
      console.error('Error saving bookmarks:', e);
    }
  }

  function saveLastPage(pdfId, page) {
    try {
      const lastPages = JSON.parse(localStorage.getItem(CONFIG.lastPageKey) || '{}');
      lastPages[pdfId] = page;
      localStorage.setItem(CONFIG.lastPageKey, JSON.stringify(lastPages));

      // Sync to Supabase
      if (window.YNS_SUPABASE && window.YNS_CONFIG?.customerId) {
        window.YNS_SUPABASE.queueSync(`pdf_progress:${pdfId}`, async () => {
          await window.YNS_SUPABASE.upsert(window.YNS_SUPABASE.tables.pdfProgress, {
            pdf_id: pdfId,
            last_page: page
          }, ['customer_id', 'pdf_id']);
        });
      }
    } catch (e) {
      console.error('Error saving last page:', e);
    }
  }

  function getLastPage(pdfId) {
    try {
      const lastPages = JSON.parse(localStorage.getItem(CONFIG.lastPageKey) || '{}');
      return lastPages[pdfId] || 1;
    } catch (e) {
      return 1;
    }
  }

  // ============================================
  // Supabase Data Loading
  // ============================================
  async function loadAnnotationsFromSupabase(pdfId) {
    if (!window.YNS_SUPABASE || !window.YNS_CONFIG?.customerId) return;

    try {
      const rows = await window.YNS_SUPABASE.select(window.YNS_SUPABASE.tables.pdfAnnotations, {
        pdf_id: pdfId
      });

      if (rows && rows.length > 0) {
        // Transform server data to local format
        const serverAnnotations = rows.map(row => ({
          id: row.id,
          type: row.annotation_type,
          page: row.page_number,
          points: row.points,
          strokeWidth: row.stroke_width,
          color: row.color,
          x: row.x_position,
          y: row.y_position,
          content: row.content,
          createdAt: row.created_at
        }));

        // Merge with local annotations
        const localAnnotations = annotations[pdfId] || [];
        annotations[pdfId] = window.YNS_SUPABASE.mergeArraysById(localAnnotations, serverAnnotations);
        localStorage.setItem(CONFIG.annotationsKey, JSON.stringify(annotations));
      }
    } catch (e) {
      console.error('[PDF] Error loading annotations from Supabase:', e);
    }
  }

  async function loadLastPageFromSupabase(pdfId) {
    if (!window.YNS_SUPABASE || !window.YNS_CONFIG?.customerId) return null;

    try {
      const rows = await window.YNS_SUPABASE.select(window.YNS_SUPABASE.tables.pdfProgress, {
        pdf_id: pdfId
      });

      if (rows && rows.length > 0) {
        const serverPage = rows[0].last_page;
        const localPage = getLastPage(pdfId);
        // Return highest page (furthest progress)
        return Math.max(serverPage, localPage);
      }
    } catch (e) {
      console.error('[PDF] Error loading last page from Supabase:', e);
    }
    return null;
  }

  // ============================================
  // PDF Loading
  // ============================================
  async function openPdf(pdfId, fileUrl, opts) {
    const pdfData = window.YNS_PDF_DATA;
    const free = !!(opts && opts.free); // free guides open straight from their URL (no library entry, no access check)
    if (!pdfData && !free) {
      console.error('PDF Data not available');
      return;
    }

    const pdf = free ? { title: opts.title || 'Free guide' } : pdfData.getPdfById(pdfId);
    if (!pdf) {
      console.error('PDF not found:', pdfId);
      return;
    }
    currentUpsell = (opts && opts.upsell) || null;
    updatePeek();

    // Check access
    if (!free && window.YNSPdfLibrary && !window.YNSPdfLibrary.hasPdfAccess(pdfId)) {
      console.warn('No access to PDF:', pdfId);
      window.location.href = pdfData.getUnlockUrl(pdfId);
      return;
    }

    currentPdfId = pdfId;
    renderPage._fitted = false; zoomRel = 1;

    // Update modal title
    const titleEl = document.getElementById('pdfViewerTitle');
    if (titleEl) {
      titleEl.textContent = pdf.title;
    }

    // Show modal
    modal.classList.add('active');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('yns-pdf-modal-open');


    // Load PDF
    try {
      showLoading(true);

      // Load annotations from Supabase in background
      loadAnnotationsFromSupabase(pdfId);

      // Use provided URL (from Edge Function) or fall back to legacy fileUrl
      const pdfUrl = fileUrl || pdf.fileUrl;
      currentUrl = pdfUrl; currentTitle = pdf.title || 'Your Nursing Space';
      const loadingTask = pdfjsLib.getDocument(pdfUrl);
      pdfDoc = await loadingTask.promise;

      totalPages = pdfDoc.numPages;
      updatePageInfo();

      // Resume from last page (check Supabase for higher page)
      let startPage = getLastPage(pdfId);
      const serverPage = await loadLastPageFromSupabase(pdfId);
      if (serverPage && serverPage > startPage) {
        startPage = serverPage;
      }
      currentPage = startPage;
      if (currentPage > totalPages) currentPage = 1;

      // Render first page
      await renderPage(currentPage);

      // Generate thumbnails
      generateThumbnails();

      // Update bookmarks panel
      updateBookmarksPanel();

      // Update annotations panel
      updateAnnotationsPanel();

      showLoading(false);

    } catch (error) {
      console.error('Error loading PDF:', error);
      showLoading(false);
      showError('Failed to load PDF. Please try again.');
    }
  }

  // ---------- Download: every copy carries the buyer's name, email and customer number ----------
  let currentUrl = null, currentTitle = '';
  function loadPdfLib() {
    if (window.PDFLib) return Promise.resolve(window.PDFLib);
    if (loadPdfLib._p) return loadPdfLib._p;
    loadPdfLib._p = new Promise((ok, fail) => {
      const sc = document.createElement('script');
      sc.src = 'https://cdn.jsdelivr.net/npm/pdf-lib@1.17.1/dist/pdf-lib.min.js';
      sc.onload = () => ok(window.PDFLib); sc.onerror = () => { loadPdfLib._p = null; fail(new Error('pdf-lib')); };
      document.head.appendChild(sc);
    });
    return loadPdfLib._p;
  }
  async function downloadWatermarked() {
    const btn = document.getElementById('pdfDownloadBtn');
    if (!btn || !currentUrl || btn.dataset.busy) return;
    const d = btn.dataset, ascii = (t) => String(t || '').replace(/[^\x20-\x7E]/g, '').replace(/\s+/g, ' ').trim();
    const email = ascii(d.wmEmail), id = ascii(d.wmId), zip = ascii(d.wmZip);
    if (!email) { toast('Log in to download your PDFs'); return; }
    btn.dataset.busy = '1'; btn.classList.add('is-busy'); toast('Preparing your download...');
    try {
      const PDFLib = await loadPdfLib();
      const res = await fetch(currentUrl); if (!res.ok) throw new Error('fetch ' + res.status);
      const doc = await PDFLib.PDFDocument.load(await res.arrayBuffer(), { ignoreEncryption: true });
      const font = await doc.embedFont(PDFLib.StandardFonts.Helvetica);
      const bold = await doc.embedFont(PDFLib.StandardFonts.HelveticaBold);
      const day = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      const line1 = 'yournursingspace.com  |  Downloaded by ' + email + (id ? '  |  Customer #' + id : '') + (zip ? '  |  Postal code ' + zip : '') + '  |  ' + day;
      const line2 = 'Personal use only. Not for sharing or resale. Got this file from someone else? Report it to support@yournursingspace.com';
      const ink = PDFLib.rgb(0.36, 0.31, 0.33);
      doc.getPages().forEach((page) => {
        const { width, height } = page.getSize();
        const fit = (t, f, max) => { let z = max; while (z > 5 && f.widthOfTextAtSize(t, z) > width - 40) z -= 0.25; return z; };
        const s1 = fit(line1, bold, 10), s2 = fit(line2, font, 9);
        // white strip behind the stamp so it never collides with the page footer
        page.drawRectangle({ x: 0, y: 0, width, height: 10 + s2 + 4 + s1 + 8, color: PDFLib.rgb(1, 1, 1), opacity: 0.92 });
        page.drawText(line1, { x: 20, y: 10 + s2 + 4, size: s1, font: bold, color: ink, opacity: 0.9 });
        page.drawText(line2, { x: 20, y: 10, size: s2, font, color: ink, opacity: 0.9 });
        const big = Math.min(28, width / 18), w = bold.widthOfTextAtSize(email, big);
        page.drawText(email, { x: width / 2 - (w * 0.82) / 2, y: height / 2 - (w * 0.57) / 2, size: big, font: bold, color: ink, opacity: 0.1, rotate: PDFLib.degrees(35) });
      });
      doc.setSubject('Licensed to ' + email + (id ? ' (customer #' + id + ')' : ''));
      doc.setKeywords([email, id ? 'customer ' + id : '', 'yournursingspace.com'].filter(Boolean));
      doc.setProducer('Your Nursing Space');
      const blob = new Blob([await doc.save()], { type: 'application/pdf' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = (ascii(currentTitle).replace(/[^\w\- ]+/g, '').trim() || 'Your Nursing Space') + '.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      toast('Downloaded');
    } catch (err) {
      console.warn('[YNS] download failed', err);
      toast('Could not prepare the download. Please try again.');
    } finally {
      delete btn.dataset.busy; btn.classList.remove('is-busy');
    }
  }

  // ---------- Sneak peek of the full book, under the last page of a free guide ----------
  let currentUpsell = null;
  function escP(t) { return String(t == null ? '' : t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
  function updatePeek() {
    const box = document.getElementById('pdfCanvasContainer'); if (!box) return;
    let el = document.getElementById('ynsPdfPeek');
    const show = !!(currentUpsell && pdfDoc && totalPages && currentPage === totalPages);
    if (!show) { if (el) el.hidden = true; return; }
    if (!el) {
      el = document.createElement('div'); el.id = 'ynsPdfPeek'; el.className = 'yns-pdf-peek'; box.appendChild(el);
      el.addEventListener('click', (e) => {
        const b = e.target.closest('[data-peek-open]'); if (!b || !currentUpsell || !currentUpsell.owned) return;
        e.preventDefault(); const go = currentUpsell.owned.go; closePdf(); setTimeout(go, 60);
      });
    }
    const u = currentUpsell;
    el.innerHTML = '<p class="yns-pdf-peek__kick">You finished the free pages</p>' +
      '<h3 class="yns-pdf-peek__title">Keep going with ' + escP(u.book) + '</h3>' +
      '<p class="yns-pdf-peek__line">' + escP(u.line) + '</p>' +
      '<div class="yns-pdf-peek__art"><img src="' + escP(u.peek) + '" alt="" loading="lazy"><span class="yns-pdf-peek__lock"><span aria-hidden="true">&#128274;</span> ' + (u.owned ? 'You own this book' : 'Unlock to read the rest') + '</span></div>' +
      (u.owned
        ? '<button type="button" class="yns-pdf-peek__btn" data-peek-open>' + escP(u.owned.label) + ' <span aria-hidden="true">&rarr;</span></button>'
        : '<a class="yns-pdf-peek__btn" href="' + escP(u.buy) + '">Unlock the full book <span aria-hidden="true">&rarr;</span></a>' +
          '<a class="yns-pdf-peek__more" href="' + escP(u.page) + '" target="_blank" rel="noopener">See what is inside first</a>');
    el.hidden = false;
  }

  function closePdf() {
    currentUpsell = null; updatePeek();
    // Save last page
    if (currentPdfId && currentPage) {
      saveLastPage(currentPdfId, currentPage);
    }

    modal.classList.remove('active');
    document.body.style.overflow = '';
    document.body.classList.remove('yns-pdf-modal-open');

    // Cleanup
    pdfDoc = null;
    currentPdfId = null;
    currentPage = 1;
    totalPages = 0;
    currentTool = null;

    // Clear canvas
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    // Clear thumbnails
    const thumbnailsPanel = document.getElementById('thumbnailsPanel');
    if (thumbnailsPanel) {
      thumbnailsPanel.innerHTML = '';
    }
  }

  // ============================================
  // Page Rendering
  // ============================================
  // ---------- Fit, zoom and sharp rendering ----------
  let fitScale = 1.2;        // scale at which the page fills the reading width
  let zoomRel = 1;           // reader's zoom relative to fit (1 = 100%)
  function computeFit(page) {
    const box = document.getElementById('pdfCanvasContainer');
    const avail = Math.max(240, (box ? box.clientWidth : 900) - 48);
    const vp = page.getViewport({ scale: 1 });
    fitScale = Math.min(avail / vp.width, 2.5);
    currentScale = fitScale * zoomRel;
  }
  async function setZoomRel(z) {
    zoomRel = Math.min(4, Math.max(0.5, Math.round(z * 100) / 100));
    currentScale = fitScale * zoomRel;
    updateZoomLevel();
    if (!pdfDoc) return;
    // keep the same spot in view while zooming
    const box = document.getElementById('pdfCanvasContainer');
    const rel = box ? { x: (box.scrollLeft + box.clientWidth / 2) / Math.max(1, box.scrollWidth), y: (box.scrollTop + box.clientHeight / 2) / Math.max(1, box.scrollHeight) } : null;
    await renderPage(currentPage);
    if (box && rel) { box.scrollLeft = rel.x * box.scrollWidth - box.clientWidth / 2; box.scrollTop = rel.y * box.scrollHeight - box.clientHeight / 2; }
  }
  function enablePan() {
    const box = document.getElementById('pdfCanvasContainer'); if (!box || box._pan) return; box._pan = true;
    let drag = null;
    box.addEventListener('pointerdown', (e) => {
      if (currentTool || e.button !== 0 || e.target.closest('textarea, input, button, .yns-pdf-note')) return;
      if (box.scrollWidth <= box.clientWidth + 2 && box.scrollHeight <= box.clientHeight + 2) return;
      drag = { x: e.clientX, y: e.clientY, l: box.scrollLeft, t: box.scrollTop };
      box.classList.add('is-panning');
    });
    window.addEventListener('pointermove', (e) => { if (!drag) return; box.scrollLeft = drag.l - (e.clientX - drag.x); box.scrollTop = drag.t - (e.clientY - drag.y); });
    window.addEventListener('pointerup', () => { if (drag) { drag = null; box.classList.remove('is-panning'); } });
  }
  function toast(msg) {
    let t = document.getElementById('ynsPdfToast');
    if (!t) { t = document.createElement('div'); t.id = 'ynsPdfToast'; t.className = 'yns-pdf-toast'; (modal || document.body).appendChild(t); }
    t.textContent = msg; t.classList.add('is-on');
    clearTimeout(t._h); t._h = setTimeout(() => t.classList.remove('is-on'), 1800);
  }
  function toolHint() {
    let h = document.getElementById('ynsPdfHint');
    if (!h) { h = document.createElement('div'); h.id = 'ynsPdfHint'; h.className = 'yns-pdf-hint'; (modal || document.body).appendChild(h); }
    const text = currentTool === 'highlight' ? 'Highlighter on: drag across the page. Pick a colour above. Esc to stop.'
      : currentTool === 'note' ? 'Notes on: click anywhere on the page to add a note. Esc to stop.' : '';
    h.textContent = text; h.classList.toggle('is-on', !!text);
  }

  async function renderPage(pageNum) {
    if (!pdfDoc || isRendering) return;

    // Cancel any in-progress drawing
    if (isDrawingHighlight) {
      const preview = document.getElementById('highlightPreview');
      if (preview) preview.remove();
      isDrawingHighlight = false;
      highlightPoints = [];
    }

    isRendering = true;
    currentPage = pageNum;

    try {
      const page = await pdfDoc.getPage(pageNum);
      if (!renderPage._fitted) { computeFit(page); renderPage._fitted = true; updateZoomLevel(); }
      const viewport = page.getViewport({ scale: currentScale });

      // Draw at screen density (sharper text on Retina/phones), shown at the page's CSS size
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      const ratio = Math.min(dpr * 1.25, 3, Math.sqrt(16e6 / Math.max(1, viewport.width * viewport.height)));
      canvas.width = Math.floor(viewport.width * ratio);
      canvas.height = Math.floor(viewport.height * ratio);
      canvas.style.width = Math.floor(viewport.width) + 'px';
      canvas.style.height = Math.floor(viewport.height) + 'px';

      // Render PDF page
      const renderContext = {
        canvasContext: ctx,
        viewport: viewport,
        transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : null
      };

      await page.render(renderContext).promise;

      // Update canvas wrapper size to match the rendered page
      if (canvasWrapper) {
        canvasWrapper.style.width = `${viewport.width}px`;
        canvasWrapper.style.height = `${viewport.height}px`;
      }

      // Ensure annotation layer exists for rendering annotations
      annotationLayer = document.getElementById('annotationLayer');

      // Render annotations for this page
      renderAnnotations(pageNum);

      // Update UI
      updatePageInfo();
      updateThumbnailSelection();
      updatePeek();

      // Save last page
      if (currentPdfId) {
        saveLastPage(currentPdfId, pageNum);
      }

    } catch (error) {
      console.error('Error rendering page:', error);
    }

    isRendering = false;
  }

  // ============================================
  // Navigation
  // ============================================
  function goToPage(pageNum) {
    if (pageNum < 1 || pageNum > totalPages) return;
    const box = document.getElementById('pdfCanvasContainer');
    renderPage(pageNum).then(() => { if (box) box.scrollTop = 0; });
  }

  function prevPage() {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  }

  function nextPage() {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    } else if (currentUpsell) {
      const pk = document.getElementById('ynsPdfPeek'); if (pk && !pk.hidden) pk.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function updatePageInfo() {
    const currentPageEl = document.getElementById('currentPage');
    const totalPagesEl = document.getElementById('totalPages');
    const prevBtn = document.getElementById('pdfPrevPage');
    const nextBtn = document.getElementById('pdfNextPage');

    if (currentPageEl) currentPageEl.textContent = currentPage;
    if (totalPagesEl) totalPagesEl.textContent = totalPages;

    if (prevBtn) prevBtn.disabled = currentPage <= 1;
    if (nextBtn) nextBtn.disabled = currentPage >= totalPages && !currentUpsell;
  }

  // ============================================
  // Zoom
  // ============================================
  function zoomIn() { setZoomRel(zoomRel + 0.25); }
  function zoomOut() { setZoomRel(zoomRel - 0.25); }

  function updateZoomLevel() {
    const zoomLevelEl = document.getElementById('pdfZoomLevel');
    if (zoomLevelEl) {
      zoomLevelEl.textContent = `${Math.round(zoomRel * 100)}%`;
      zoomLevelEl.title = 'Click to fit the page to the screen';
    }
  }

  // ============================================
  // Thumbnails
  // ============================================
  async function generateThumbnails() {
    const panel = document.getElementById('thumbnailsPanel');
    if (!panel || !pdfDoc) return;

    panel.innerHTML = '';

    for (let i = 1; i <= Math.min(totalPages, 20); i++) {
      const page = await pdfDoc.getPage(i);
      const viewport = page.getViewport({ scale: 0.2 });

      const thumbCanvas = document.createElement('canvas');
      thumbCanvas.width = viewport.width;
      thumbCanvas.height = viewport.height;

      const thumbCtx = thumbCanvas.getContext('2d');
      await page.render({
        canvasContext: thumbCtx,
        viewport: viewport
      }).promise;

      const thumbWrapper = document.createElement('div');
      thumbWrapper.className = 'yns-thumbnail-wrapper';
      thumbWrapper.dataset.page = i;

      thumbWrapper.innerHTML = `
        <div class="yns-thumbnail ${i === currentPage ? 'active' : ''}">
          <span class="yns-thumbnail-num">${i}</span>
        </div>
      `;

      thumbWrapper.querySelector('.yns-thumbnail').prepend(thumbCanvas);

      thumbWrapper.addEventListener('click', () => goToPage(i));

      panel.appendChild(thumbWrapper);
    }

    // Load more indicator if needed
    if (totalPages > 20) {
      const moreIndicator = document.createElement('div');
      moreIndicator.className = 'yns-thumbnails-more';
      moreIndicator.textContent = `+${totalPages - 20} more pages`;
      moreIndicator.style.cursor = 'pointer';
      moreIndicator.title = 'Click to load all thumbnails';

      moreIndicator.addEventListener('click', async () => {
        moreIndicator.textContent = 'Loading...';
        moreIndicator.style.cursor = 'default';

        for (let i = 21; i <= totalPages; i++) {
          const page = await pdfDoc.getPage(i);
          const viewport = page.getViewport({ scale: 0.2 });

          const thumbCanvas = document.createElement('canvas');
          thumbCanvas.width = viewport.width;
          thumbCanvas.height = viewport.height;

          const thumbCtx = thumbCanvas.getContext('2d');
          await page.render({
            canvasContext: thumbCtx,
            viewport: viewport
          }).promise;

          const thumbWrapper = document.createElement('div');
          thumbWrapper.className = 'yns-thumbnail-wrapper';
          thumbWrapper.dataset.page = i;

          thumbWrapper.innerHTML = `
            <div class="yns-thumbnail ${i === currentPage ? 'active' : ''}">
              <span class="yns-thumbnail-num">${i}</span>
            </div>
          `;

          thumbWrapper.querySelector('.yns-thumbnail').prepend(thumbCanvas);
          thumbWrapper.addEventListener('click', () => goToPage(i));

          panel.insertBefore(thumbWrapper, moreIndicator);
        }

        moreIndicator.remove();
      });

      panel.appendChild(moreIndicator);
    }
  }

  function updateThumbnailSelection() {
    const thumbnails = document.querySelectorAll('.yns-thumbnail');
    thumbnails.forEach((thumb, index) => {
      thumb.classList.toggle('active', index + 1 === currentPage);
    });
  }

  // ============================================
  // Tools
  // ============================================
  function setTool(tool) {
    currentTool = currentTool === tool ? null : tool;

    // Update tool buttons
    document.querySelectorAll('.yns-pdf-tool').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.tool === currentTool);
    });

    // Show/hide color picker for highlight tool
    const colorPicker = document.getElementById('highlightColorPicker');
    if (colorPicker) {
      colorPicker.style.display = currentTool === 'highlight' ? 'flex' : 'none';
    }

    toolHint();

    // Update cursor on wrapper
    const wrapper = document.getElementById('pdfCanvasWrapper');
    if (wrapper) {
      wrapper.style.cursor = (currentTool === 'highlight' || currentTool === 'note') ? 'crosshair' : '';
    }
  }

  function setHighlightColor(color) {
    currentHighlightColor = color;

    // Update color buttons
    document.querySelectorAll('.yns-color-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.color === color);
    });
  }

  // ============================================
  // Annotations
  // ============================================
  function addAnnotation(type, data) {
    if (!currentPdfId) return;

    // Initialize annotations array for this PDF
    if (!annotations[currentPdfId]) {
      annotations[currentPdfId] = [];
    }

    const annotation = {
      id: generateId(),
      type,
      page: currentPage,
      ...data,
      createdAt: new Date().toISOString()
    };

    annotations[currentPdfId].push(annotation);
    saveAnnotations();
    toast(type === 'note' ? 'Note saved' : type === 'highlight' ? 'Highlight saved' : 'Saved');

    // Re-render annotations
    renderAnnotations(currentPage);
    updateAnnotationsPanel();

    // Dispatch event
    document.dispatchEvent(new CustomEvent('yns:annotationAdded', {
      detail: {
        pdfId: currentPdfId,
        annotation
      },
      bubbles: true
    }));

    return annotation;
  }

  function removeAnnotation(annotationId) {
    if (!currentPdfId || !annotations[currentPdfId]) return;

    annotations[currentPdfId] = annotations[currentPdfId].filter(a => a.id !== annotationId);
    saveAnnotations();

    // Delete from Supabase
    if (window.YNS_SUPABASE && window.YNS_CONFIG?.customerId) {
      window.YNS_SUPABASE.queueSync(`delete_annotation:${annotationId}`, async () => {
        await window.YNS_SUPABASE.delete(window.YNS_SUPABASE.tables.pdfAnnotations, {
          id: annotationId
        });
      });
    }

    renderAnnotations(currentPage);
    updateAnnotationsPanel();
  }

  function renderAnnotations(pageNum) {
    // Query fresh to ensure we have the current layer
    const layer = document.getElementById('annotationLayer');
    if (!layer || !currentPdfId) return;

    // Clear existing annotations
    layer.innerHTML = '';

    const pageAnnotations = (annotations[currentPdfId] || []).filter(a => a.page === pageNum);

    pageAnnotations.forEach(annotation => {
      const el = createAnnotationElement(annotation);
      if (el) {
        layer.appendChild(el);
      }
    });
  }

  function createAnnotationElement(annotation) {
    switch (annotation.type) {
      case 'highlight':
        return createHighlightElement(annotation);
      case 'note':
        return createNoteElement(annotation);
      default:
        return null;
    }
  }

  function createHighlightElement(annotation) {
    // Check if this is a new freeform path highlight or legacy rectangle
    if (annotation.points) {
      // New freeform path rendering
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'yns-pdf-highlight-svg');
      svg.setAttribute('viewBox', '0 0 100 100');
      svg.setAttribute('preserveAspectRatio', 'none');
      svg.style.position = 'absolute';
      svg.style.left = '0';
      svg.style.top = '0';
      svg.style.width = '100%';
      svg.style.height = '100%';
      svg.style.pointerEvents = 'none';
      svg.style.overflow = 'visible';
      svg.dataset.annotationId = annotation.id;

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', pointsToSmoothPath(annotation.points));
      path.setAttribute('stroke', annotation.color);
      path.setAttribute('stroke-width', annotation.strokeWidth || 3);
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      path.setAttribute('fill', 'none');
      path.setAttribute('opacity', '0.4');
      path.style.pointerEvents = 'stroke';
      path.setAttribute('class', 'yns-pdf-highlight-path');

      svg.appendChild(path);

      // Create a wrapper div for the delete button positioning
      const wrapper = document.createElement('div');
      wrapper.className = 'yns-pdf-highlight-wrapper';
      wrapper.dataset.annotationId = annotation.id;

      // Position the delete button at the start of the path
      const firstPoint = annotation.points[0];
      wrapper.style.position = 'absolute';
      wrapper.style.left = firstPoint.x + '%';
      wrapper.style.top = firstPoint.y + '%';

      // Delete button
      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'yns-pdf-annotation-delete';
      deleteBtn.innerHTML = '×';
      deleteBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        removeAnnotation(annotation.id);
      });
      wrapper.appendChild(deleteBtn);

      // Add click handler to path for showing delete button
      path.addEventListener('click', (e) => {
        e.stopPropagation();
        // Toggle delete button visibility
        wrapper.classList.toggle('show-delete');
      });

      // Return a container with both elements
      const container = document.createDocumentFragment();
      container.appendChild(svg);
      container.appendChild(wrapper);

      return container;
    } else if (annotation.width !== undefined) {
      // Legacy rectangle rendering (for existing annotations)
      const el = document.createElement('div');
      el.className = 'yns-pdf-highlight';
      el.dataset.annotationId = annotation.id;
      el.style.cssText = `
        position: absolute;
        left: ${annotation.x}%;
        top: ${annotation.y}%;
        width: ${annotation.width}%;
        height: ${annotation.height}%;
        background: ${annotation.color};
        opacity: 0.4;
        pointer-events: auto;
        cursor: pointer;
        border-radius: 2px;
      `;

      // Delete button
      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'yns-pdf-annotation-delete';
      deleteBtn.innerHTML = '×';
      deleteBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        removeAnnotation(annotation.id);
      });
      el.appendChild(deleteBtn);

      el.addEventListener('click', (e) => {
        if (e.target === deleteBtn) return;
        e.stopPropagation();
        showAnnotationMenu(annotation, e);
      });

      return el;
    }
    return null;
  }

  function createNoteElement(annotation) {
    const el = document.createElement('div');
    el.className = 'yns-pdf-note';
    el.dataset.annotationId = annotation.id;
    el.style.cssText = `
      position: absolute;
      left: ${annotation.x}%;
      top: ${annotation.y}%;
    `;

    // Content div (visible text)
    const content = document.createElement('div');
    content.className = 'yns-pdf-note-content';
    content.textContent = annotation.content;
    el.appendChild(content);

    // Delete button
    const deleteBtn = document.createElement('button');
    deleteBtn.className = 'yns-pdf-annotation-delete';
    deleteBtn.innerHTML = '×';
    deleteBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      removeAnnotation(annotation.id);
    });
    el.appendChild(deleteBtn);

    // Click to edit
    el.addEventListener('click', (e) => {
      if (e.target === deleteBtn) return;
      startEditingNote(el, annotation);
    });

    // Make draggable
    setupNoteDrag(el, annotation);

    return el;
  }

  // Convert points array to smooth SVG path using quadratic bezier curves
  function pointsToSmoothPath(points) {
    if (!points || points.length === 0) return '';
    if (points.length === 1) {
      return `M ${points[0].x} ${points[0].y} L ${points[0].x} ${points[0].y}`;
    }

    let d = `M ${points[0].x} ${points[0].y}`;

    for (let i = 1; i < points.length - 1; i++) {
      const midX = (points[i].x + points[i + 1].x) / 2;
      const midY = (points[i].y + points[i + 1].y) / 2;
      d += ` Q ${points[i].x} ${points[i].y} ${midX} ${midY}`;
    }

    // Last point
    const last = points[points.length - 1];
    d += ` L ${last.x} ${last.y}`;

    return d;
  }

  // Start editing a note inline
  function startEditingNote(el, annotation) {
    if (el.classList.contains('editing')) return;
    el.classList.add('editing');

    const content = el.querySelector('.yns-pdf-note-content');
    const originalText = content.textContent;

    // Replace content with textarea
    const textarea = document.createElement('textarea');
    textarea.className = 'yns-pdf-note-textarea';
    textarea.value = originalText;
    content.style.display = 'none';
    el.insertBefore(textarea, content);
    textarea.focus();
    textarea.select();

    const saveEdit = () => {
      const newText = textarea.value.trim();
      if (newText && newText !== originalText) {
        updateAnnotationContent(annotation.id, newText);
        content.textContent = newText;
      }
      textarea.remove();
      content.style.display = '';
      el.classList.remove('editing');
    };

    textarea.addEventListener('blur', saveEdit);
    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        saveEdit();
      }
      if (e.key === 'Escape') {
        textarea.remove();
        content.style.display = '';
        el.classList.remove('editing');
      }
    });
  }

  // Make notes draggable
  function setupNoteDrag(el, annotation) {
    let isDragging = false;
    let hasMoved = false;
    let dragStartX, dragStartY, initialX, initialY;

    el.addEventListener('mousedown', (e) => {
      if (e.target.classList.contains('yns-pdf-annotation-delete')) return;
      if (el.classList.contains('editing')) return;

      isDragging = true;
      hasMoved = false;
      el.classList.add('dragging');
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      initialX = annotation.x;
      initialY = annotation.y;
      e.preventDefault();
    });

    const handleDrag = (e) => {
      if (!isDragging || !annotationLayer) return;
      const rect = annotationLayer.getBoundingClientRect();
      const deltaX = ((e.clientX - dragStartX) / rect.width) * 100;
      const deltaY = ((e.clientY - dragStartY) / rect.height) * 100;

      if (Math.abs(deltaX) > 1 || Math.abs(deltaY) > 1) hasMoved = true;

      const newX = Math.max(0, Math.min(95, initialX + deltaX));
      const newY = Math.max(0, Math.min(95, initialY + deltaY));
      el.style.left = newX + '%';
      el.style.top = newY + '%';
    };

    const handleDragEnd = () => {
      if (!isDragging) return;
      isDragging = false;
      el.classList.remove('dragging');

      if (hasMoved) {
        const newX = parseFloat(el.style.left);
        const newY = parseFloat(el.style.top);
        updateAnnotationPosition(annotation.id, newX, newY);
      }
    };

    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', handleDragEnd);
  }

  // Update annotation position in storage
  function updateAnnotationPosition(annotationId, newX, newY) {
    if (!currentPdfId || !annotations[currentPdfId]) return;
    const annotation = annotations[currentPdfId].find(a => a.id === annotationId);
    if (annotation) {
      annotation.x = newX;
      annotation.y = newY;
      saveAnnotations();
      dispatchAnnotationUpdate();
    }
  }

  // Update annotation content in storage
  function updateAnnotationContent(annotationId, newContent) {
    if (!currentPdfId || !annotations[currentPdfId]) return;
    const annotation = annotations[currentPdfId].find(a => a.id === annotationId);
    if (annotation) {
      annotation.content = newContent;
      saveAnnotations();
      dispatchAnnotationUpdate();
    }
  }

  // Dispatch event to sync with study mode
  function dispatchAnnotationUpdate() {
    document.dispatchEvent(new CustomEvent('yns:annotationsUpdated', {
      detail: { pdfId: currentPdfId },
      bubbles: true
    }));
  }

  function showAnnotationMenu(annotation, event) {
    // Remove existing menus
    document.querySelectorAll('.yns-annotation-menu').forEach(m => m.remove());

    const menu = document.createElement('div');
    menu.className = 'yns-annotation-menu';
    menu.innerHTML = `
      <button class="yns-menu-item" data-action="delete">
        <span>🗑️</span> Delete
      </button>
    `;

    menu.style.cssText = `
      position: fixed;
      left: ${event.clientX}px;
      top: ${event.clientY}px;
      z-index: 100001;
    `;

    menu.querySelector('[data-action="delete"]').addEventListener('click', () => {
      removeAnnotation(annotation.id);
      menu.remove();
    });

    document.body.appendChild(menu);

    // Close menu on click outside
    setTimeout(() => {
      document.addEventListener('click', function closeMenu() {
        menu.remove();
        document.removeEventListener('click', closeMenu);
      });
    }, 10);
  }

  function showNotePopup(annotation) {
    const popup = document.createElement('div');
    popup.className = 'yns-note-popup';
    popup.innerHTML = `
      <div class="yns-note-popup-content">
        <h4>Note</h4>
        <p>${annotation.content}</p>
        <div class="yns-note-popup-actions">
          <button class="yns-note-delete">Delete</button>
          <button class="yns-note-close">Close</button>
        </div>
      </div>
    `;

    popup.querySelector('.yns-note-delete').addEventListener('click', () => {
      removeAnnotation(annotation.id);
      popup.remove();
    });

    popup.querySelector('.yns-note-close').addEventListener('click', () => {
      popup.remove();
    });

    document.body.appendChild(popup);
  }

  // ============================================
  // Bookmarks
  // ============================================
  function toggleBookmark() {
    if (!currentPdfId) return;

    if (!bookmarks[currentPdfId]) {
      bookmarks[currentPdfId] = [];
    }

    const existingIndex = bookmarks[currentPdfId].findIndex(b => b.page === currentPage);

    if (existingIndex >= 0) {
      // Remove bookmark
      bookmarks[currentPdfId].splice(existingIndex, 1);
    } else {
      // Add bookmark
      bookmarks[currentPdfId].push({
        page: currentPage,
        createdAt: new Date().toISOString()
      });
    }

    saveBookmarks();
    updateBookmarksPanel();
    updateBookmarkButton();
    toast(existingIndex >= 0 ? 'Bookmark removed' : 'Page ' + currentPage + ' bookmarked');
  }

  function isPageBookmarked(pageNum) {
    if (!currentPdfId || !bookmarks[currentPdfId]) return false;
    return bookmarks[currentPdfId].some(b => b.page === pageNum);
  }

  function updateBookmarkButton() {
    const bookmarkBtn = document.querySelector('[data-tool="bookmark"]');
    if (bookmarkBtn) {
      bookmarkBtn.classList.toggle('active', isPageBookmarked(currentPage));
    }
  }

  function updateBookmarksPanel() {
    const panel = document.getElementById('bookmarksPanel');
    if (!panel || !currentPdfId) return;

    const pdfBookmarks = bookmarks[currentPdfId] || [];

    if (pdfBookmarks.length === 0) {
      panel.innerHTML = '<p class="yns-panel-empty">No bookmarks yet. Click the bookmark icon to add one.</p>';
      return;
    }

    panel.innerHTML = pdfBookmarks
      .sort((a, b) => a.page - b.page)
      .map(b => `
        <div class="yns-bookmark-item" data-page="${b.page}">
          <span class="yns-bookmark-icon">🔖</span>
          <span class="yns-bookmark-label">Page ${b.page}</span>
        </div>
      `)
      .join('');

    panel.querySelectorAll('.yns-bookmark-item').forEach(item => {
      item.addEventListener('click', () => {
        goToPage(parseInt(item.dataset.page));
      });
    });
  }

  // ============================================
  // Annotations Panel
  // ============================================
  function updateAnnotationsPanel() {
    const panel = document.getElementById('annotationsPanel');
    if (!panel || !currentPdfId) return;

    const pdfAnnotations = annotations[currentPdfId] || [];

    if (pdfAnnotations.length === 0) {
      panel.innerHTML = '<p class="yns-panel-empty">No annotations yet. Use the tools above to add highlights and notes.</p>';
      return;
    }

    // Group by page
    const byPage = {};
    pdfAnnotations.forEach(a => {
      if (!byPage[a.page]) byPage[a.page] = [];
      byPage[a.page].push(a);
    });

    panel.innerHTML = Object.entries(byPage)
      .sort(([a], [b]) => parseInt(a) - parseInt(b))
      .map(([page, items]) => `
        <div class="yns-annotations-group">
          <h4 class="yns-annotations-page">Page ${page}</h4>
          ${items.map(a => `
            <div class="yns-annotation-item" data-id="${a.id}" data-page="${page}">
              <span class="yns-annotation-type">${a.type === 'highlight' ? '🖍️' : '📝'}</span>
              <span class="yns-annotation-preview">${a.content || 'Highlight'}</span>
            </div>
          `).join('')}
        </div>
      `)
      .join('');

    panel.querySelectorAll('.yns-annotation-item').forEach(item => {
      item.addEventListener('click', () => {
        goToPage(parseInt(item.dataset.page));
      });
    });
  }

  // ============================================
  // Event Listeners
  // ============================================
  function setupEventListeners() {
    // Close button
    const closeBtn = document.getElementById('pdfViewerClose');
    if (closeBtn) {
      closeBtn.addEventListener('click', closePdf);
    }

    // Navigation
    document.getElementById('pdfPrevPage')?.addEventListener('click', prevPage);
    document.getElementById('pdfNextPage')?.addEventListener('click', nextPage);

    // Zoom
    document.getElementById('pdfZoomIn')?.addEventListener('click', zoomIn);
    document.getElementById('pdfZoomOut')?.addEventListener('click', zoomOut);
    document.getElementById('pdfZoomLevel')?.addEventListener('click', () => setZoomRel(1));
    document.getElementById('pdfDownloadBtn')?.addEventListener('click', downloadWatermarked);
    enablePan();
    document.getElementById('pdfCanvasContainer')?.addEventListener('wheel', (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      clearTimeout(setZoomRel._t);
      const next = zoomRel * (e.deltaY < 0 ? 1.08 : 1 / 1.08);
      zoomRel = Math.min(4, Math.max(0.5, next)); updateZoomLevel();
      setZoomRel._t = setTimeout(() => setZoomRel(zoomRel), 90);
    }, { passive: false });
    window.addEventListener('resize', () => { if (!pdfDoc || !modal?.classList.contains('active')) return; clearTimeout(window._ynsFitT); window._ynsFitT = setTimeout(async () => { const pg = await pdfDoc.getPage(currentPage); computeFit(pg); renderPage(currentPage); }, 200); });

    // Tools
    document.querySelectorAll('.yns-pdf-tool').forEach(btn => {
      btn.addEventListener('click', () => {
        const tool = btn.dataset.tool;
        if (tool === 'bookmark') {
          toggleBookmark();
        } else {
          setTool(tool);
        }
      });
    });

    // Color picker
    document.querySelectorAll('.yns-color-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        setHighlightColor(btn.dataset.color);
      });
    });

    // Sidebar tabs
    document.querySelectorAll('.yns-sidebar-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        const panelId = tab.dataset.sidebar;

        document.querySelectorAll('.yns-sidebar-tab').forEach(t => t.classList.remove('active'));
        document.querySelectorAll('.yns-sidebar-panel').forEach(p => p.classList.remove('active'));

        tab.classList.add('active');
        document.getElementById(`${panelId}Panel`)?.classList.add('active');
      });
    });

    // Attach annotation events to the WRAPPER (not the annotation layer)
    // The wrapper always exists and receives events reliably
    const wrapper = document.getElementById('pdfCanvasWrapper');
    if (wrapper) {
      wrapper.addEventListener('click', handleAnnotationClick);
      wrapper.addEventListener('mousedown', handleAnnotationMouseDown);
      wrapper.addEventListener('mousemove', handleAnnotationMouseMove);
      wrapper.addEventListener('mouseup', handleAnnotationMouseUp);
      wrapper.addEventListener('mouseleave', handleAnnotationMouseLeave);
      wrapper.addEventListener('contextmenu', handleAnnotationContextMenu);
    }

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!modal?.classList.contains('active')) return;

      switch (e.key) {
        case 'ArrowLeft':
          prevPage();
          break;
        case 'ArrowRight':
          nextPage();
          break;
        case 'Escape':
          if (currentTool) { setTool(currentTool); break; }
          closePdf();
          break;
        case '+':
        case '=':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            zoomIn();
          }
          break;
        case '-':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            zoomOut();
          }
          break;
      }
    });

    // Close on backdrop click
    modal?.addEventListener('click', (e) => {
      if (e.target === modal) {
        closePdf();
      }
    });
  }

  // Handle click for note tool (attached to wrapper)
  function handleAnnotationClick(e) {
    // Only handle if a tool is active and we're not clicking an existing annotation
    if (!currentTool || currentTool === 'bookmark') return;
    if (e.target.closest('.yns-pdf-note, .yns-pdf-highlight, .yns-pdf-highlight-wrapper')) return;

    if (currentTool === 'note') {
      const wrapper = document.getElementById('pdfCanvasWrapper');
      const rect = wrapper.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      // Create note input at position
      createNoteInput(x, y);
    }
  }

  // Create note input textarea at the given position
  function createNoteInput(x, y) {
    const layer = document.getElementById('annotationLayer');
    if (!layer) return;

    const textarea = document.createElement('textarea');
    textarea.className = 'yns-pdf-note-input';
    textarea.style.left = x + '%';
    textarea.style.top = y + '%';
    textarea.placeholder = 'Type your note...';
    layer.appendChild(textarea);
    textarea.focus();

    let saved = false;

    const saveNote = () => {
      if (saved) return;
      saved = true;
      const content = textarea.value.trim();
      if (content) {
        addAnnotation('note', { x, y, content });
      }
      textarea.remove();
    };

    textarea.addEventListener('blur', saveNote);
    textarea.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' && !ev.shiftKey) {
        ev.preventDefault();
        saveNote();
      }
      if (ev.key === 'Escape') {
        ev.stopPropagation();
        saved = true;
        textarea.remove();
      }
    });
  }

  // Handle mousedown for freeform highlight drawing (attached to wrapper)
  function handleAnnotationMouseDown(e) {
    if (currentTool !== 'highlight') return;
    if (e.target.closest('.yns-pdf-note, .yns-pdf-highlight, .yns-pdf-highlight-wrapper')) return;

    const wrapper = document.getElementById('pdfCanvasWrapper');
    const rect = wrapper.getBoundingClientRect();
    const startX = ((e.clientX - rect.left) / rect.width) * 100;
    const startY = ((e.clientY - rect.top) / rect.height) * 100;

    highlightPoints = [{ x: startX, y: startY }];
    isDrawingHighlight = true;

    createHighlightPreview(startX, startY);
  }

  // Create the SVG preview element for freeform drawing
  function createHighlightPreview(startX, startY) {
    const layer = document.getElementById('annotationLayer');
    if (!layer) return;

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.id = 'highlightPreview';
    svg.setAttribute('class', 'yns-pdf-highlight-svg');
    svg.setAttribute('viewBox', '0 0 100 100');
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.style.position = 'absolute';
    svg.style.left = '0';
    svg.style.top = '0';
    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.pointerEvents = 'none';
    svg.style.overflow = 'visible';

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', `M ${startX} ${startY}`);
    path.setAttribute('stroke', currentHighlightColor);
    path.setAttribute('stroke-width', '3');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('fill', 'none');
    path.setAttribute('opacity', '0.5');
    path.id = 'highlightPreviewPath';

    svg.appendChild(path);
    layer.appendChild(svg);
  }

  // Handle mousemove for tracking highlight path (attached to wrapper)
  function handleAnnotationMouseMove(e) {
    if (!isDrawingHighlight || highlightPoints.length === 0) return;

    const path = document.getElementById('highlightPreviewPath');
    if (!path) return;

    const wrapper = document.getElementById('pdfCanvasWrapper');
    const rect = wrapper.getBoundingClientRect();
    const currentX = ((e.clientX - rect.left) / rect.width) * 100;
    const currentY = ((e.clientY - rect.top) / rect.height) * 100;

    // Only add point if moved enough (>0.5% of page) to avoid too many points
    const lastPoint = highlightPoints[highlightPoints.length - 1];
    const distance = Math.sqrt(Math.pow(currentX - lastPoint.x, 2) + Math.pow(currentY - lastPoint.y, 2));

    if (distance > 0.5) {
      highlightPoints.push({ x: currentX, y: currentY });
      path.setAttribute('d', pointsToSmoothPath(highlightPoints));
    }
  }

  // Handle mouseup to save the annotation (attached to wrapper)
  function handleAnnotationMouseUp(e) {
    if (!isDrawingHighlight || highlightPoints.length === 0) return;

    const preview = document.getElementById('highlightPreview');
    if (preview) preview.remove();

    // Only create highlight if we have enough points (meaningful stroke)
    if (highlightPoints.length >= 2) {
      addAnnotation('highlight', {
        points: highlightPoints,
        strokeWidth: 3,
        color: currentHighlightColor
      });
    }

    isDrawingHighlight = false;
    highlightPoints = [];
  }

  // Handle mouseleave to cancel drawing (attached to wrapper)
  function handleAnnotationMouseLeave() {
    if (isDrawingHighlight) {
      const preview = document.getElementById('highlightPreview');
      if (preview) preview.remove();
      isDrawingHighlight = false;
      highlightPoints = [];
    }
  }

  // Handle right-click context menu to delete highlights
  function handleAnnotationContextMenu(e) {
    // Check if right-clicking on a highlight path or wrapper
    const highlightPath = e.target.closest('.yns-pdf-highlight-path');
    const highlightWrapper = e.target.closest('.yns-pdf-highlight-wrapper');
    const highlightRect = e.target.closest('.yns-pdf-highlight');

    if (highlightPath || highlightWrapper || highlightRect) {
      e.preventDefault();

      // Get annotation ID from the element or its parent SVG
      let annotationId;
      if (highlightPath) {
        annotationId = highlightPath.closest('svg').dataset.annotationId;
      } else if (highlightWrapper) {
        annotationId = highlightWrapper.dataset.annotationId;
      } else if (highlightRect) {
        annotationId = highlightRect.dataset.annotationId;
      }

      if (annotationId) {
        removeAnnotation(annotationId);
      }
    }
  }

  // ============================================
  // Utility Functions
  // ============================================
  function generateId() {
    return 'ann_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  function showLoading(show) {
    if (canvasContainer) {
      canvasContainer.classList.toggle('loading', show);
    }
  }

  function showError(message) {
    alert(message); // Simple implementation - could be a toast
  }

  // ============================================
  // Public API
  // ============================================
  window.YNSPdfViewer = {
    openPdf,
    closePdf,
    goToPage,
    prevPage,
    nextPage,
    zoomIn,
    zoomOut,
    setTool,
    addAnnotation,
    removeAnnotation,
    toggleBookmark,
    getAnnotations: (pdfId) => annotations[pdfId] || [],
    getBookmarks: (pdfId) => bookmarks[pdfId] || []
  };

  // ============================================
  // Initialize
  // ============================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

// Add PDF viewer specific styles
const viewerStyles = document.createElement('style');
viewerStyles.textContent = `
  .yns-pdf-canvas-container.loading::after {
    content: 'Loading...';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: white;
    padding: 20px 40px;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  }

  .yns-pdf-canvas-container.tool-active {
    cursor: crosshair;
  }

  .yns-thumbnail-wrapper {
    padding: 4px;
  }

  .yns-thumbnail {
    position: relative;
    border: 2px solid transparent;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .yns-thumbnail:hover {
    border-color: var(--yns-sage-light);
  }

  .yns-thumbnail.active {
    border-color: var(--yns-sage);
  }

  .yns-thumbnail canvas {
    display: block;
    width: 100%;
    height: auto;
  }

  .yns-thumbnail-num {
    position: absolute;
    bottom: 2px;
    right: 2px;
    background: rgba(0,0,0,0.6);
    color: white;
    font-size: 0.625rem;
    padding: 2px 4px;
    border-radius: 2px;
  }

  .yns-thumbnails-more {
    text-align: center;
    padding: 8px;
    font-size: 0.75rem;
    color: var(--yns-text-muted);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .yns-thumbnails-more:hover {
    color: var(--yns-sage);
    background: var(--yns-sage-muted);
    border-radius: 4px;
  }

  .yns-panel-empty {
    font-size: 0.8125rem;
    color: var(--yns-text-muted);
    text-align: center;
    padding: 20px 10px;
  }

  .yns-bookmark-item,
  .yns-annotation-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.15s ease;
    font-size: 0.8125rem;
  }

  .yns-bookmark-item:hover,
  .yns-annotation-item:hover {
    background: var(--yns-sage-muted);
  }

  .yns-annotations-group {
    margin-bottom: 12px;
  }

  .yns-annotations-page {
    font-size: 0.75rem;
    color: var(--yns-text-muted);
    margin: 0 0 4px 0;
    padding-bottom: 4px;
    border-bottom: 1px solid var(--yns-sage-border);
  }

  .yns-annotation-preview {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .yns-annotation-menu {
    background: white;
    border: 1px solid var(--yns-sage-border);
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
    overflow: hidden;
  }

  .yns-menu-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border: none;
    background: none;
    width: 100%;
    text-align: left;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .yns-menu-item:hover {
    background: var(--yns-sage-muted);
  }

  .yns-note-popup {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100002;
  }

  .yns-note-popup-content {
    background: white;
    border-radius: 12px;
    padding: 20px;
    max-width: 400px;
    width: 90%;
  }

  .yns-note-popup-content h4 {
    margin: 0 0 8px 0;
    font-size: 1rem;
  }

  .yns-note-popup-content p {
    margin: 0 0 16px 0;
    color: var(--yns-text-secondary);
  }

  .yns-note-popup-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }

  .yns-note-popup-actions button {
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 0.875rem;
    cursor: pointer;
    border: none;
  }

  .yns-note-delete {
    background: var(--yns-error-soft);
    color: var(--yns-error);
  }

  .yns-note-close {
    background: var(--yns-sage);
    color: white;
  }

  /* Annotation layer - only for rendering annotations, not for events */
  #annotationLayer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 5;
  }

  /* SVG Freeform Highlight Paths */
  .yns-pdf-highlight-svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    overflow: visible;
    z-index: 5;
  }

  .yns-pdf-highlight-svg path {
    pointer-events: stroke;
    cursor: pointer;
    transition: opacity 0.15s ease;
  }

  .yns-pdf-highlight-svg path:hover {
    opacity: 0.6 !important;
  }

  /* Wrapper for SVG highlight delete button */
  .yns-pdf-highlight-wrapper {
    position: absolute;
    width: 0;
    height: 0;
    z-index: 10;
  }

  .yns-pdf-highlight-wrapper .yns-pdf-annotation-delete {
    position: absolute;
    top: -12px;
    left: -12px;
    opacity: 0;
  }

  .yns-pdf-highlight-wrapper:hover .yns-pdf-annotation-delete,
  .yns-pdf-highlight-wrapper.show-delete .yns-pdf-annotation-delete {
    opacity: 1;
  }

  /* Legacy rectangle highlights */
  .yns-pdf-highlight {
    position: absolute;
    opacity: 0.4;
    cursor: pointer;
    pointer-events: auto;
    border-radius: 2px;
    transition: opacity 0.15s ease;
  }

  .yns-pdf-highlight:hover {
    opacity: 0.6;
  }

  /* Full sticky note display */
  .yns-pdf-note {
    position: absolute;
    min-width: 120px;
    max-width: 200px;
    padding: 8px 12px;
    background: rgba(255, 249, 196, 0.95);
    border-radius: 4px;
    box-shadow: 2px 2px 8px rgba(0,0,0,0.15);
    cursor: grab;
    pointer-events: auto;
    z-index: 10;
    transition: transform 0.1s, box-shadow 0.1s;
  }

  .yns-pdf-note:hover {
    box-shadow: 3px 3px 12px rgba(0,0,0,0.2);
  }

  .yns-pdf-note.dragging {
    cursor: grabbing;
    transform: scale(1.02) rotate(1deg);
    box-shadow: 4px 4px 16px rgba(0,0,0,0.25);
    z-index: 100;
  }

  .yns-pdf-note.editing {
    cursor: default;
  }

  /* Note content text */
  .yns-pdf-note-content {
    font-size: 13px;
    line-height: 1.4;
    color: #333;
    word-wrap: break-word;
    white-space: pre-wrap;
  }

  /* Inline edit textarea */
  .yns-pdf-note-textarea {
    width: 100%;
    min-height: 40px;
    padding: 0;
    border: none;
    background: transparent;
    font-size: 13px;
    line-height: 1.4;
    font-family: inherit;
    color: #333;
    resize: vertical;
  }

  .yns-pdf-note-textarea:focus {
    outline: none;
  }

  /* New note input */
  .yns-pdf-note-input {
    position: absolute;
    min-width: 150px;
    min-height: 60px;
    padding: 8px 12px;
    background: rgba(255, 249, 196, 0.95);
    border: 2px solid #B8976A;
    border-radius: 4px;
    box-shadow: 2px 2px 8px rgba(0,0,0,0.15);
    font-size: 13px;
    line-height: 1.4;
    font-family: inherit;
    color: #333;
    resize: both;
    z-index: 100;
  }

  .yns-pdf-note-input:focus {
    outline: none;
    border-color: #46523E;
  }

  /* Delete button on annotations */
  .yns-pdf-annotation-delete {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 18px;
    height: 18px;
    background: #D32F2F;
    color: white;
    border: none;
    border-radius: 50%;
    font-size: 12px;
    line-height: 1;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 15;
  }

  .yns-pdf-note .yns-pdf-annotation-delete {
    top: -6px;
    right: -6px;
  }

  .yns-pdf-highlight:hover .yns-pdf-annotation-delete,
  .yns-pdf-note:hover .yns-pdf-annotation-delete {
    opacity: 1;
  }
`;
document.head.appendChild(viewerStyles);
